import React, { useEffect } from 'react'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import Icone from '../../Icone'
import { Text } from '@mantine/core'
import { useListState } from '@mantine/hooks'
import * as S from './styles'

const EXAMPLE_DATA = [
  { id: 1, name: 'Item 1' },
  { id: 2, name: 'Item 2', description: 'Com descrição' },
  { id: 3, name: 'Item 3', icon: 'eye' },
  {
    id: 4,
    name: 'Item 4',
    description: 'Completo com descrição e ícone',
    icon: 'eye-off'
  }
]

export const DragItemPreview = ({ item, icon }) => {
  return (
    <S.Item
      $isDragging
      style={{
        opacity: 1,
        cursor: 'grabbing',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'none',
        marginBottom: 0
      }}
    >
      <S.DragHandle>
        <Icone nome="grip" color="default" width="24px" height="24px" />
      </S.DragHandle>
      <S.ItemContent>
        <Text>{item.name}</Text>
        {item.description && (
          <Text c="dimmed" size="sm">
            {item.description}
          </Text>
        )}
      </S.ItemContent>
      <Icone
        style={{ marginLeft: 'auto' }}
        nome={item.icon || icon}
        color="default"
        width="24px"
        height="24px"
      />
    </S.Item>
  )
}

const SortableItem = ({ item, icon, onIconClick, draggable }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: item.id,
    disabled: !draggable
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const handleIconClick = (e) => {
    e.stopPropagation()
    if (onIconClick) {
      onIconClick(item.id)
    }
  }

  return (
    <S.Item
      ref={setNodeRef}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      style={style}
      $isDragging={isDragging}
      $draggable={draggable}
    >
      {draggable && (
        <S.DragHandle>
          <Icone nome="grip" color="default" width="24px" height="24px" />
        </S.DragHandle>
      )}
      <S.ItemContent>
        <Text>{item.name}</Text>
        {item.description && (
          <Text c="dimmed" size="sm">
            {item.description}
          </Text>
        )}
      </S.ItemContent>
      <div
        style={{
          marginLeft: 'auto',
          cursor: onIconClick ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center'
        }}
        onClick={onIconClick ? handleIconClick : undefined}
        role={onIconClick ? 'button' : undefined}
        onKeyDown={
          onIconClick
            ? (e) => e.key === 'Enter' && handleIconClick(e)
            : undefined
        }
      >
        <Icone
          nome={item.icon || icon}
          color="default"
          opacity={80}
          width="24px"
          height="24px"
        />
      </div>
    </S.Item>
  )
}

const DroppableContainer = ({ id, children }) => {
  const { setNodeRef } = useDroppable({
    id
  })

  return <div ref={setNodeRef}>{children}</div>
}

export default function DndListHandle({
  data = EXAMPLE_DATA,
  icon,
  groupId,
  activeId = null,
  onDragStart,
  onIconClick,
  draggable = true,
  onOrderChange
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  )

  const usedSensors = sensors
  const [state, handlers] = useListState(groupId ? data : data)

  useEffect(() => {
    if (!groupId) {
      handlers.setState(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, groupId])

  const handleDragStart = (event) => {
    if (onDragStart) {
      onDragStart(event)
    }
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const oldIndex = state.findIndex((i) => i.id === active.id)
    const newIndex = state.findIndex((i) => i.id === over.id)
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(state, oldIndex, newIndex)
      handlers.setState(newOrder)
      if (onOrderChange) {
        onOrderChange(newOrder)
      }
      const order = newOrder.map((item, i) => ({
        order: i,
        id: item.id,
        name: item.name
      }))
    }
  }

  const itemsToRender = groupId ? data : state
  const activeItem = activeId
    ? itemsToRender.find((item) => item.id === activeId)
    : null

  const sortableContent = (
    <SortableContext
      items={itemsToRender.map((i) => i.id)}
      strategy={verticalListSortingStrategy}
    >
      {itemsToRender.map((item) => (
        <SortableItem
          key={item.id}
          item={item}
          icon={icon}
          onIconClick={onIconClick}
          draggable={draggable}
        />
      ))}
    </SortableContext>
  )

  if (groupId) {
    return (
      <DroppableContainer id={groupId}>{sortableContent}</DroppableContainer>
    )
  }

  return (
    <DndContext
      sensors={usedSensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {sortableContent}
      {activeItem && (
        <DragOverlay>
          <DragItemPreview item={activeItem} icon={icon} />
        </DragOverlay>
      )}
    </DndContext>
  )
}
